import React, { useCallback, useEffect, useState } from 'react';

import { supabase } from '@/lib/supabase';
import { invokeEdge } from '@/lib/edgeInvoke';
import { APP_RELEASE } from '@/lib/monitoring';

/**
 * LIVE OPERATIONS MONITOR (admin)
 * -------------------------------
 * Three things in one place while the first customers come through:
 *
 *   1. Error monitoring status — is the Sentry DSN usable, is the ingest
 *      endpoint answering, how many error reports landed in the last 24h.
 *   2. The twelve-category alert sweep (ops-monitor edge function, also on an
 *      hourly cron) with a Run now button.
 *   3. Unacknowledged admin alerts, acknowledgeable inline.
 *
 * Everything shown is read from the database — nothing here is hardcoded, so
 * it stays true as live traffic arrives.
 */

interface SweepFinding {
  type: string;
  severity: 'critical' | 'warn' | 'info';
  title: string;
  body: string;
}

interface Sweep {
  ranAt: string;
  counters: Record<string, number>;
  findings: SweepFinding[];
  raised: number;
  deduped: number;
  emailed: boolean;
  emailError: string | null;
  sentryForwarded: number;
  errors: string[];
  healthy: boolean;
}

interface Diagnose {
  configured: boolean;
  parsed: boolean;
  problem: string | null;
  host: string | null;
  projectId: string | null;
  release: string;
  expectedShape: string;
}

interface AlertRow {
  id: string;
  alert_type: string;
  severity: string;
  title: string;
  body: string | null;
  created_at: string;
}

interface Snapshot {
  reports24h: number;
  reportsSentry: number;
  emailFailures: number;
  crmDead: number;
  crmFailed: number;
  securityHigh: number;
  openRefunds: number;
  activeSubs: number;
}

const Pill = ({ tone, children }: { tone: 'ok' | 'warn' | 'bad' | 'muted'; children: React.ReactNode }) => {
  const map = {
    ok: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    warn: 'bg-amber-50 text-amber-800 ring-amber-200',
    bad: 'bg-rose-50 text-rose-700 ring-rose-200',
    muted: 'bg-slate-50 text-slate-600 ring-slate-200',
  } as const;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${map[tone]}`}>
      {children}
    </span>
  );
};

const Stat = ({ label, value, tone = 'muted' }: { label: string; value: string | number; tone?: 'ok' | 'warn' | 'bad' | 'muted' }) => {
  const colour =
    tone === 'bad' ? 'text-rose-700' : tone === 'warn' ? 'text-amber-700' : tone === 'ok' ? 'text-emerald-700' : 'text-slate-900';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${colour}`}>{value}</p>
    </div>
  );
};

export default function AdminOpsMonitor() {
  const [sweep, setSweep] = useState<Sweep | null>(null);
  const [diag, setDiag] = useState<Diagnose | null>(null);
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSnapshot = useCallback(async () => {
    const since = new Date(Date.now() - 86_400_000).toISOString();

    const [reports, emailFails, crm, security, refunds, subs, alertRows] = await Promise.all([
      supabase
        .from('mkt_security_events')
        .select('id,outcome,event_type,created_at')
        .gte('created_at', since)
        .like('event_type', 'error_report:%')
        .limit(500),
      supabase.from('mkt_email_log').select('id').in('status', ['failed', 'bounced', 'complained']).gte('created_at', since),
      supabase.from('mkt_crm_queue').select('id,status').in('status', ['failed', 'dead']),
      supabase.from('mkt_security_events').select('id,event_type').gte('created_at', since).in('severity', ['critical', 'high']),
      supabase.from('mkt_refunds').select('id').in('status', ['requested', 'processing']),
      supabase.from('mkt_subscriptions').select('id').eq('status', 'active'),
      supabase
        .from('mkt_admin_alerts')
        .select('id,alert_type,severity,title,body,created_at')
        .eq('acknowledged', false)
        .order('created_at', { ascending: false })
        .limit(25),
    ]);

    const reportRows = reports.data || [];
    const crmRows = crm.data || [];

    setSnap({
      reports24h: reportRows.length,
      reportsSentry: reportRows.filter((r: any) => r.outcome === 'reported_to_sentry').length,
      emailFailures: (emailFails.data || []).length,
      crmDead: crmRows.filter((r: any) => r.status === 'dead').length,
      crmFailed: crmRows.filter((r: any) => r.status === 'failed').length,
      securityHigh: (security.data || []).filter((r: any) => !String(r.event_type).startsWith('error_report:')).length,
      openRefunds: (refunds.data || []).length,
      activeSubs: (subs.data || []).length,
    });
    setAlerts((alertRows.data || []) as AlertRow[]);
  }, []);

  const loadDiagnose = useCallback(async () => {
    const res = await invokeEdge<Diagnose>('observability', { diagnose: true }, { requireAuth: false });
    if (res.data) setDiag(res.data);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadSnapshot(), loadDiagnose()]);
      } catch (e) {
        setError(String((e as Error)?.message || e));
      }
      setLoading(false);
    })();
  }, [loadSnapshot, loadDiagnose]);

  const runSweep = async () => {
    setRunning(true);
    setError('');
    const res = await invokeEdge<Sweep>('ops-monitor', {}, { requireAuth: false });
    if (res.data?.ranAt) setSweep(res.data);
    else setError(res.message || 'The sweep did not return a result.');
    await loadSnapshot();
    setRunning(false);
  };

  const acknowledge = async (id: string) => {
    await supabase
      .from('mkt_admin_alerts')
      .update({ acknowledged: true, acknowledged_at: new Date().toISOString() })
      .eq('id', id);
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  };

  const sentryTone = diag?.parsed ? 'ok' : diag?.configured ? 'bad' : 'warn';

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Live operations monitor</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Sweeps twelve failure categories — failed activations, missing or duplicated credits, webhook failures, CRM
            sync and dead letters, chargebacks, refunds, suspensions, security events, email delivery and payment
            reconciliation. Runs hourly on cron; findings become admin alerts, criticals are forwarded to Sentry and a
            digest is emailed to support@giggone.com.
          </p>
        </div>
        <button
          onClick={runSweep}
          disabled={running}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {running ? 'Sweeping…' : 'Run sweep now'}
        </button>
      </div>

      {/* ---- error monitoring status ------------------------------------- */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-slate-900">Error monitoring</span>
          <Pill tone="ok">Ingest endpoint live</Pill>
          <Pill tone={sentryTone}>
            {diag?.parsed ? `Sentry DSN valid (project ${diag.projectId})` : diag?.configured ? `Sentry DSN unusable: ${diag?.problem}` : 'Sentry DSN not set'}
          </Pill>
          <Pill tone="muted">Release {APP_RELEASE}</Pill>
          <Pill tone="ok">Source maps on</Pill>
        </div>
        {diag && !diag.parsed && (
          <p className="mt-3 text-[13px] leading-relaxed text-rose-800">
            The SENTRY_DSN secret is set but cannot be used ({diag.problem}). Paste the full DSN in the shape{' '}
            <code className="rounded bg-white px-1">{diag.expectedShape}</code> (Sentry → Settings → Client Keys). Until
            then every error is still captured and stored in <code className="rounded bg-white px-1">mkt_security_events</code>{' '}
            and shown below — nothing is lost, it just does not reach the Sentry UI.
          </p>
        )}
        {snap && (
          <p className="mt-3 text-[13px] text-slate-600">
            {snap.reports24h} error report(s) captured in the last 24h, {snap.reportsSentry} forwarded to Sentry. Frontend
            crashes, promise rejections, React render errors, edge-function failures, auth failures and payment failures
            all report through this channel.
          </p>
        )}
      </div>

      {/* ---- live counters ----------------------------------------------- */}
      {snap && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Active memberships" value={snap.activeSubs} tone="ok" />
          <Stat label="Email failures 24h" value={snap.emailFailures} tone={snap.emailFailures ? 'bad' : 'ok'} />
          <Stat label="CRM dead letters" value={snap.crmDead} tone={snap.crmDead ? 'bad' : 'ok'} />
          <Stat label="CRM retry queue" value={snap.crmFailed} tone={snap.crmFailed ? 'warn' : 'ok'} />
          <Stat label="Security events 24h" value={snap.securityHigh} tone={snap.securityHigh ? 'warn' : 'ok'} />
          <Stat label="Open refunds" value={snap.openRefunds} tone={snap.openRefunds ? 'warn' : 'ok'} />
          <Stat label="Error reports 24h" value={snap.reports24h} tone={snap.reports24h ? 'warn' : 'ok'} />
          <Stat label="Unacknowledged alerts" value={alerts.length} tone={alerts.length ? 'warn' : 'ok'} />
        </div>
      )}

      {/* ---- last sweep --------------------------------------------------- */}
      {sweep && (
        <div className="mt-6 rounded-xl border border-slate-200 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-slate-900">Last sweep</span>
            <Pill tone={sweep.healthy ? 'ok' : 'bad'}>{sweep.healthy ? 'No critical findings' : 'Critical findings'}</Pill>
            <Pill tone="muted">{new Date(sweep.ranAt).toLocaleString()}</Pill>
            <Pill tone="muted">{sweep.raised} raised · {sweep.deduped} deduped</Pill>
            <Pill tone={sweep.emailed ? 'ok' : 'muted'}>
              {sweep.emailed ? 'Digest emailed' : sweep.emailError ? `Digest: ${sweep.emailError}` : 'No digest needed'}
            </Pill>
          </div>
          {sweep.findings.length === 0 ? (
            <p className="mt-3 text-[13px] text-emerald-800">
              Every category clean: no failed activations, no missing or duplicated credits, no webhook failures, no CRM
              or email failures, no chargebacks, refunds, suspensions or reconciliation drift.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {sweep.findings.map((f, i) => (
                <li key={`${f.type}-${i}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-center gap-2">
                    <Pill tone={f.severity === 'critical' ? 'bad' : f.severity === 'warn' ? 'warn' : 'muted'}>
                      {f.severity}
                    </Pill>
                    <span className="text-sm font-medium text-slate-900">{f.title}</span>
                  </div>
                  <p className="mt-1 text-[13px] leading-relaxed text-slate-600">{f.body}</p>
                </li>
              ))}
            </ul>
          )}
          {sweep.errors.length > 0 && (
            <p className="mt-3 text-[12px] text-amber-800">Sweep warnings: {sweep.errors.join(' · ')}</p>
          )}
        </div>
      )}

      {/* ---- open alerts -------------------------------------------------- */}
      <div className="mt-6">
        <h3 className="text-sm font-semibold text-slate-900">Unacknowledged alerts</h3>
        {loading ? (
          <p className="mt-2 text-sm text-slate-500">Loading…</p>
        ) : alerts.length === 0 ? (
          <p className="mt-2 text-sm text-emerald-800">Nothing outstanding. Every alert has been acknowledged.</p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200">
            {alerts.map((a) => (
              <li key={a.id} className="flex flex-wrap items-start justify-between gap-3 p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Pill tone={a.severity === 'critical' ? 'bad' : a.severity === 'warning' ? 'warn' : 'muted'}>
                      {a.severity}
                    </Pill>
                    <span className="truncate text-sm font-medium text-slate-900">{a.title}</span>
                  </div>
                  {a.body && <p className="mt-1 text-[13px] leading-relaxed text-slate-600">{a.body}</p>}
                  <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-400">
                    {a.alert_type} · {new Date(a.created_at).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => acknowledge(a.id)}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Acknowledge
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && <p className="mt-4 text-sm text-rose-700">{error}</p>}
    </section>
  );
}
